import type { ReactNode } from 'react'
import {
	BackdropDesktop,
	Card,
	ErrorCodeText,
	LocationAddress,
	LocationInfo,
	LocationLogo,
	LocationName,
	MessageText,
	StatusPill,
	StatusPillText,
} from './StoreClosed.styled'
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined'
import { useFormatMessage } from '@/translations/useFormatMessage'

interface StoreClosedProps {
	locationName?: string
	address?: string
	logoUrl?: string | null
	message?: string | null
	statusIcon?: ReactNode
	statusText?: string
	/** Rendered on a separate line below the message. Used for POS_CONFIGURATION errors
	 *  where the backend error code must be surfaced to the operator for support. */
	errorCode?: string
}

export function StoreClosed({
	locationName,
	address,
	logoUrl,
	message,
	statusIcon,
	statusText,
	errorCode,
}: StoreClosedProps) {
	const { t } = useFormatMessage()

	const body = message ?? t('component:storeClosed:message:fallback')
	const pillIcon = statusIcon ?? (
		<StorefrontOutlinedIcon sx={{ fontSize: 24 }} aria-hidden="true" />
	)
	const pillText = statusText ?? t('component:storeClosed:statusPill')

	return (
		<BackdropDesktop
			role="main"
			aria-labelledby={locationName ? 'store-closed-title' : undefined}
			aria-label={!locationName ? pillText : undefined}
		>
			<Card>
				{logoUrl && (
					<LocationLogo src={logoUrl} alt={t('component:storeClosed:image:alt')} />
				)}
				{(locationName || address) && (
					<LocationInfo>
						{locationName && (
							<LocationName id="store-closed-title">{locationName}</LocationName>
						)}
						{address && <LocationAddress>{address}</LocationAddress>}
					</LocationInfo>
				)}
				<StatusPill>
					{pillIcon}
					<StatusPillText>{pillText}</StatusPillText>
				</StatusPill>
				<MessageText>{body}</MessageText>
				{errorCode && (
					<ErrorCodeText>
						{t('component:storeClosed:errorCode', { code: errorCode })}
					</ErrorCodeText>
				)}
			</Card>
		</BackdropDesktop>
	)
}
